import React from 'react';
import { Divider, Paper, Stack, Typography } from '@mui/material';

interface ParsedDataField {
  label: string;
  getValue: (data: Record<string, unknown>) => string | null;
}

// Extend this array as new AI agent parsed data shapes are introduced
const PARSED_DATA_FIELDS: ParsedDataField[] = [
  { label: 'Lender', getValue: d => (d.lenderName as string) ?? null },
  { label: 'Decision', getValue: d => (d.decisionType as string) ?? null },
  {
    label: 'Amount',
    getValue: d =>
      d.approvalAmount != null
        ? `$${Number(d.approvalAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : null,
  },
  { label: 'Term', getValue: d => (d.termLengthMonths != null ? `${d.termLengthMonths} months` : null) },
  {
    label: 'Monthly Payment',
    getValue: d =>
      d.monthlyPayment != null
        ? `$${Number(d.monthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : null,
  },
  { label: 'Interest Rate', getValue: d => (d.interestRate != null ? `${d.interestRate}%` : null) },
];

interface ParsedDataSectionProps {
  payload: Record<string, unknown> | null | undefined;
}

export const ParsedDataSection = ({ payload }: ParsedDataSectionProps) => {
  if (!payload) return null;

  const parsedData = payload.parsedData as Record<string, unknown> | null | undefined;
  if (!parsedData) return null;

  const fields = PARSED_DATA_FIELDS.map(({ label, getValue }) => ({ label, value: getValue(parsedData) })).filter(
    f => f.value !== null
  );

  const stipulations = Array.isArray(parsedData.stipulations) ? (parsedData.stipulations as string[]) : [];

  if (fields.length === 0 && stipulations.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Parsed Data Summary
      </Typography>
      <Stack>
        {fields.map(({ label, value }, index) => (
          <React.Fragment key={label}>
            {index > 0 && <Divider />}
            <Stack direction="row" spacing={1} sx={{ py: 0.75 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                {label}
              </Typography>
              <Typography variant="body2">{value}</Typography>
            </Stack>
          </React.Fragment>
        ))}

        {stipulations.length > 0 && (
          <>
            {fields.length > 0 && <Divider />}
            <Stack sx={{ py: 0.75 }} spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Conditions and Stipulations
              </Typography>
              <Stack spacing={0.5}>
                {stipulations.map((item, index) => (
                  <Stack key={index} direction="row" spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 20, flexShrink: 0 }}>
                      {index + 1}.
                    </Typography>
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};
